/**
 * syncLog — a small local record of what sync actually did.
 *
 * WHY (Walter 2026-08-12). Every sync investigation this month ran the same
 * way: he screenshots a conflict modal hours after the fact, and I infer the
 * mechanism backwards from what's on screen. Twice the decisive clue was a
 * MISSING label. That's an expensive way to answer "what happened", and it
 * only works when there's a conflict left to look at — the failures that
 * merely delayed a sync leave nothing behind at all.
 *
 * So: record the decisions as they happen, on the device, and let him paste
 * the log to me. Sizes and short fingerprints only — never note CONTENT, so
 * the log is safe to share and stays small. localStorage only: no Firestore
 * reads or writes, which was his explicit constraint.
 */

import { getWindowLabel } from './windowId';

const KEY = 'lists_sync_log_v1';
const MAX_ENTRIES = 150;
// Belt-and-braces: localStorage is ~5MB per origin and shared with the offline
// queue, which holds real note content and must never be evicted for a log.
const MAX_BYTES = 60 * 1024;

/**
 * A cheap, stable fingerprint. NOT cryptographic — it exists so two versions
 * of a list can be compared ("same base?") in a log line without ever writing
 * the text down. djb2, base36, 7 chars.
 */
export function fingerprint(text) {
  const s = String(text ?? '');
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36).slice(0, 7);
}

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return []; // corrupt / unavailable — start fresh rather than throw
  }
}

function write(entries) {
  try {
    let out = entries.slice(-MAX_ENTRIES);
    let json = JSON.stringify(out);
    // Trim oldest until it fits. A log that evicts the offline queue would be
    // strictly worse than no log at all.
    while (json.length > MAX_BYTES && out.length > 1) {
      out = out.slice(Math.ceil(out.length / 4));
      json = JSON.stringify(out);
    }
    localStorage.setItem(KEY, json);
  } catch {
    // Quota or private mode — logging must never break the app.
  }
}

/**
 * logSync(event, detail) — append one line.
 *   event  — short kebab-case verb: 'save-failed', 'queued', 'drained',
 *            'receive-merge', 'receive-conflict', 'adopt'.
 *   detail — small flat object. Keep values short; no note content.
 */
export function logSync(event, detail = {}) {
  try {
    const entries = read();
    // `w` = which WINDOW (lib/windowId), not which device. Two windows on one
    // profile share a clientId, so without this a two-window fight over the
    // same list is invisible in the log (Walter 2026-09-04).
    entries.push({ t: new Date().toISOString(), e: String(event), w: getWindowLabel(), ...detail });
    write(entries);
  } catch {
    // Never throw from a logger.
  }
}

export function readSyncLog() {
  return read();
}

export function clearSyncLog() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

/**
 * formatSyncLog — plain text for pasting into a chat. One line per entry,
 * newest last, so the tail is the interesting part.
 */
export function formatSyncLog(entries = read()) {
  if (!entries.length) return 'sync log is empty — nothing recorded yet.';
  const lines = entries.map((x) => {
    const { t, e, ...rest } = x;
    const when = typeof t === 'string' ? t.replace('T', ' ').slice(0, 19) : '?';
    const detail = Object.entries(rest)
      .map(([k, v]) => `${k}=${v}`)
      .join(' ');
    return `${when}  ${e}${detail ? '  ' + detail : ''}`;
  });
  return `listicity sync log (${entries.length} entries, newest last)\n${lines.join('\n')}`;
}

// ── Heartbeats (Walter 2026-08-21) ──────────────────────────────
// The log recorded only FAILURES, which turned out to be half a story. When
// his PC's text vanished after the phone typed, the PC's log was empty — and
// an empty log is equally consistent with "saved fine" and "never tried".
// Nothing recorded RECEIVING at all, so there was no way to tell a deaf
// device from a quiet one. Two investigations stalled on exactly that.
//
// So log the healthy path too, throttled hard so it can never crowd out the
// failures: at most one line per list per two minutes, per kind.
const HEARTBEAT_MS = 2 * 60 * 1000;
const lastHeartbeat = new Map();

export function logHeartbeat(event, key, detail = {}) {
  const k = `${event}:${key}`;
  const now = Date.now();
  const prev = lastHeartbeat.get(k) || 0;
  if (now - prev < HEARTBEAT_MS) return;
  lastHeartbeat.set(k, now);
  logSync(event, detail);
}
